import { cva, type VariantProps } from 'class-variance-authority'
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Checkbox}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports three sizes: sm (h-4 w-4), md (h-5 w-5, default), lg (h-6 w-6)
 * - Focus ring uses the brand primary color
 * - Checked state uses the brand primary background
 *
 * @example
 * ```tsx
 * const classes = checkboxVariants({ size: 'md' })
 * // Returns: "h-5 w-5 rounded-ds-sm border ..."
 * ```
 */
export const checkboxVariants = cva(
	// React Aria puts `data-selected` on the rendered label, not on this box, so the
	// checked treatment is reached through the label's `group` rather than off `&`.
	'rounded-ds-sm border-2 border-ds-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 group-data-[selected]:bg-ds-primary group-data-[selected]:border-ds-primary group-data-[selected]:text-ds-primary-foreground group-data-[indeterminate]:bg-ds-primary group-data-[indeterminate]:border-ds-primary group-data-[indeterminate]:text-ds-primary-foreground',
	{
		variants: {
			size: {
				sm: 'h-4 w-4',
				md: 'h-5 w-5',
				lg: 'h-6 w-6',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Checkbox} component.
 *
 * Extends React Aria's CheckboxProps with CVA size variants.
 *
 * @property size - Checkbox size: 'sm' (h-4 w-4) | 'md' (h-5 w-5, default) | 'lg' (h-6 w-6)
 * @property className - Optional additional Tailwind classes
 * @property children - Label text
 * @property isDisabled - Disabled state (React Aria pattern)
 * @property isIndeterminate - Indeterminate state (tri-state checkbox)
 * @property isSelected - Controlled checked state
 *
 * @example
 * ```tsx
 * <Checkbox size="md" isSelected={true}>
 *   Accept terms
 * </Checkbox>
 * ```
 */
export interface CheckboxProps extends Omit<AriaCheckboxProps, 'className'>, VariantProps<typeof checkboxVariants> {
	className?: string
}

/**
 * Accessible checkbox primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation (Space to toggle), focus management,
 *   and ARIA attributes provided by React Aria's `<Checkbox>` primitive.
 * - **Styling:** All colors, spacing, and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-4 w-4), medium (h-5 w-5, default), large (h-6 w-6).
 * - **Indeterminate:** Supports tri-state (unchecked, checked, indeterminate).
 * - **Focus indicator:** 2px ring in the brand primary color.
 * - **Checked state:** the brand primary color background.
 *
 * @example Basic usage
 * ```tsx
 * import { Checkbox } from '@atlas/ui-primitives'
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <Checkbox>Accept terms and conditions</Checkbox>
 *       <Checkbox isRequired>I agree to the privacy policy</Checkbox>
 *     </form>
 *   )
 * }
 * ```
 *
 * @example With controlled state
 * ```tsx
 * const [isChecked, setIsChecked] = useState(false)
 *
 * <Checkbox
 *   isSelected={isChecked}
 *   onChange={setIsChecked}
 * >
 *   Subscribe to newsletter
 * </Checkbox>
 * ```
 *
 * @example Indeterminate state
 * ```tsx
 * <Checkbox isIndeterminate>
 *   Select all (some selected)
 * </Checkbox>
 * ```
 *
 * @see {@link checkboxVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/checkbox/checkbox.stories.tsx for visual examples
 */
export function Checkbox({ size, className, children, ...props }: CheckboxProps) {
	return (
		<AriaCheckbox
			{...props}
			className="group relative flex cursor-pointer items-center gap-2 data-[disabled]:cursor-default"
		>
			<div className={cn(checkboxVariants({ size }), className)}>
				<svg
					className="h-full w-full stroke-current"
					viewBox="0 0 24 24"
					fill="none"
					strokeWidth="3"
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					{props.isIndeterminate ? (
						<line x1="6" y1="12" x2="18" y2="12" />
					) : (
						<polyline points="20 6 9 17 4 12" className="opacity-0 group-data-[selected]:opacity-100" />
					)}
				</svg>
			</div>
			{children && <span className="text-ds-base text-ds-text select-none">{children as React.ReactNode}</span>}
		</AriaCheckbox>
	)
}
